import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MfaEmailIcon from '../../../../assets/images/icons/mfa-email-img.png';
import MfaPhoneIcon from '../../../../assets/images/icons/mfa-phone-img.png';

import { useAppDispatch, useAppSelector } from '../../../../store';
import MFAMethodButton, { MFAMethodType } from '../../../../components/common/MfaButton';

import alert from '../../../../utils/alert';
import { user } from '../../../account/api/accountSlice';

import { useNavigate, Link } from 'react-router-dom';
import useMfaEnabledFlag from '../../../../hooks/useIsMfaEnabledFlag';
import {
  updateMfaPreferenceThunk,
  getMfaPreferenceThunk,
  getSmsConsentThunk,
  updateSmsConsentThunk,
} from '../../../auth/api/mfaSlice';
import './MfaAuthentication.scss';

const TwoFactorAuthentication = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [selectedMethod, setSelectedMethod] = useState('');
  const [isMethodUpdating, setIsMethodUpdating] = useState(false);
  const [smsConsentChecked, setSmsConsentChecked] = useState(false);
  const userInfo = useAppSelector(user);
  const phone = userInfo?.telecom.find((item: any) => item.system === 'phone')?.value ?? '';
  const email = userInfo?.telecom.find((item: any) => item.system === 'email')?.value ?? '';
  const loginEmail = email || '';

  const isMfaEnabled = useMfaEnabledFlag();

  useEffect(() => {
    if (!isMfaEnabled) {
      navigate('/app/settings');
    }
  }, [isMfaEnabled, navigate]);

  const onSelectMethod = useCallback(
    async (method: MFAMethodType) => {
      setIsMethodUpdating(true);

      try {
        const result = await dispatch(
          updateMfaPreferenceThunk({
            email: loginEmail,
            mfaType: method,
          }),
        ).unwrap();

        if (result.statusCode === 200) {
          setSelectedMethod(method);
        } else {
          setSelectedMethod('');
          alert.error(t('Failed to update verification method. Please try again.'));
        }
      } catch (error) {
        setSelectedMethod('');
      } finally {
        setIsMethodUpdating(false);
      }
    },
    [dispatch, loginEmail, t],
  );

  useEffect(() => {
    const fetchMfaPreference = async () => {
      try {
        const result = await dispatch(getMfaPreferenceThunk(loginEmail)).unwrap();
        if (result.resource) {
          setSelectedMethod(result.resource);
        }
      } catch (error) {
        console.error('Failed to load default MFA method:', error);
      }
    };

    if (loginEmail) {
      fetchMfaPreference();
    }
  }, [dispatch, loginEmail, t]);

  useEffect(() => {
    if (selectedMethod !== 'sms') {
      setSmsConsentChecked(false);
    }
  }, [selectedMethod]);

  useEffect(() => {
    if (selectedMethod === 'sms') {
      console.log('Patient Info:', userInfo);
    }
  }, [selectedMethod, userInfo]);

  useEffect(() => {
    const fetchSmsConsent = async () => {
      if (selectedMethod === 'sms' && userInfo?.uuid) {
        try {
          const result = await dispatch(getSmsConsentThunk(userInfo.uuid)).unwrap();
          if (result.resource?.sms_consent !== undefined) {
            setSmsConsentChecked(result.resource.sms_consent);
          }
        } catch (error) {
          console.error('Failed to load SMS consent:', error);
        }
      }
    };

    fetchSmsConsent();
  }, [selectedMethod, userInfo?.uuid, dispatch]);

  const handleSmsConsentChange = useCallback(
    async (checked: boolean) => {
      setSmsConsentChecked(checked);
      if (userInfo?.uuid) {
        try {
          await dispatch(
            updateSmsConsentThunk({ patientId: userInfo.uuid, smsConsent: checked }),
          ).unwrap();
        } catch (error) {
          console.error('Failed to update SMS consent:', error);
          setSmsConsentChecked(!checked);
          alert.error(t('Failed to update SMS consent. Please try again.'));
        }
      }
    },
    [dispatch, userInfo?.uuid, t],
  );

  const renderVerificationMethod = useMemo(
    () => (
      <div>
        <MFAMethodButton
          method='sms'
          label={t('Text Message')}
          value={phone}
          isSelected={selectedMethod === 'sms'}
          isMethodUpdating={isMethodUpdating}
          onSelect={onSelectMethod}
          icon={MfaPhoneIcon}
          showUnselectedState={false}
        />
        <MFAMethodButton
          method='email'
          label={t('Email')}
          value={email}
          isSelected={selectedMethod === 'email'}
          isMethodUpdating={isMethodUpdating}
          onSelect={onSelectMethod}
          icon={MfaEmailIcon}
          showUnselectedState={false}
        />
      </div>
    ),
    [selectedMethod, isMethodUpdating, onSelectMethod, t, email, phone],
  );

  return (
    <div style={{ maxWidth: '480px' }}>
      {renderVerificationMethod}
      {selectedMethod === 'sms' && (
        <div className='sms-consent-container'>
          <div className='sms-consent-checkbox-wrapper'>
            <input
              type='checkbox'
              id='sms-consent-checkbox'
              className='sms-consent-checkbox'
              checked={smsConsentChecked}
              onChange={(e) => handleSmsConsentChange(e.target.checked)}
            />
          </div>
          <label htmlFor='sms-consent-checkbox' className='sms-consent-label'>
            I agree to receive SMS text messages from Direct2Care at this number for authentication
            codes. Message and data rates may apply. Reply STOP to unsubscribe.{' '}
            <Link
              to='/app/settings/privacy'
              style={{ color: '#0086c9', textDecoration: 'underline' }}
              onClick={(e) => e.stopPropagation()}
            >
              Privacy Policy
            </Link>
            .
          </label>
        </div>
      )}
    </div>
  );
};

export default TwoFactorAuthentication;
